<template>
  <div class="saving-insight-popup">
    <insight-details-popup
      :cashflow-category="cashflowCategory"
      :insight="insight"
      :buttons="buttons"
      :showFeedbackFooter="false"
      :close-action="closeAction">

      <template v-slot:top-content>
        <div class="insight-header">
          <div>אפשר לחסוך כל חודש עוד</div>
          <div class="amount">{{ formattedAmount }} ₪</div>
        </div>
      </template>

      <template v-slot:bottom-content>
        <div>
          <div class="insight-content">
            <div>
              יש לך חיסכון חודשי
              <span class="emphasise">במיטב</span>
              <span class="emphasise expense">(כל הכבוד!)</span>
              לפי 6 החודשים האחרונים, נראה שאפשר להעביר עוד כסף לחיסכון כל חודש.
            </div>
            <div class="emphasise" style="margin-top: 28px">
              זה לוקח פחות מדקה.
            </div>
          </div>
          <callout iconPath="icons/heart-icon.svg" title="טיפ מאיתנו" class="insight-callout">
            <template v-slot:text>
              אם לא מתאים לך כרגע, אפשר גם לעשות הפקדה חד-פעמית.</template>
          </callout>
        </div>
      </template>
      <template v-slot:footer>
        <div @click="dismissInsight" class="footer-btn">הבנתי, אל תציגו לי יותר</div>
      </template>
    </insight-details-popup>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import DDLogs from '@/DDLogs';
import BaseUI from '@riseupil/base-ui';
import Segment from '@/Segment';
import moneyUtils from '@/utils/money';
import DynamicDepositFlow from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/personalized-savings/DynamicDepositFlow';
import InsightDetailsPopup from '../InsightDetailsPopup';

export default {
  name: 'MeitavMonthlySavingPopup',
  components: {
    InsightDetailsPopup,
    Callout: BaseUI.Callout,
  },
  props: {
    insight: { type: Object, require: true },
    cashflowCategory: { type: String, required: true },
    closeAction: { type: Function, required: true },
  },
  created() {
    Segment.trackUserGot('MeitavMonthlySavingPopupLoaded', this.insight);
  },
  computed: {
    formattedAmount() {
      return moneyUtils.formatAmountWithFraction(this.insight.proposedMonthlySavingAmount, 0);
    },
    buttons() {
      return [
        {
          text: 'להגדלת הסכום החודשי',
          variant: 'primary',
          action: () => {
            Segment.trackUserInteraction('MeitavMonthlySavingConfirmedEdit', this.insight);
            this.setMonthlyDeposit();
            this.closeModalAndGoToFlow();
          },
        },
        {
          text: 'להפקדה חד-פעמית',
          variant: 'secondary',
          action: () => {
            Segment.trackUserInteraction('MeitavMonthlySavingConfirmedAdd', this.insight);
            this.setOpportunisticDeposit();
            this.closeModalAndGoToFlow();
          },
        },
      ];
    },
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal', 'closeModal']),
    ...mapActions('insights', ['markAsRead']),
    ...mapMutations('dynamicSavings', ['setProposedSavingsAmount', 'setOpportunisticDeposit', 'setMonthlyDeposit']),
    closeModalAndGoToFlow() {
      this.setProposedSavingsAmount(moneyUtils.extractRoundedIntegerPart(this.insight.proposedMonthlySavingAmount));
      this.closeModal();
      this.openModal({
        component: DynamicDepositFlow,
        popupAlignment: 'full-screen',
        props: { insight: this.insight },
      });
    },
    dismissInsight() {
      Segment.trackUserInteraction('MeitavMonthlySavingPopupDismissed', this.insight);
      this.closeModal();
      this.markAsRead(this.insight).catch(error => {
        DDLogs.error('Failed to mark insight as read', { insight: this.insight, error });
      });
    },
  },
};

</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';

.saving-insight-popup {
  text-align: right;

  .close-button {
    width: 100%;
  }

  .insight-header {
    color: $riseup_white;
    font-size: 16px;
    text-align: right;

    .amount {
      font-size: 46px;
      font-weight: bold;
      margin-top: 10px;

      .nis {
        font-size: 32px;
      }
    }
  }

  .emphasise {
    font-weight: bold;
  }

  .expense {
    color: $riseup_red;
  }

  .insight-content {
    text-align: right;
    font-size: 22px;
    line-height: 28px;

    .text-buttom {
      margin-top: 24px;
    }
  }

  .insight-callout {
    margin-top: 33px;
  }

  .footer-btn {
    margin-bottom: unset;
    margin-top: unset;
  }
}

</style>
